<template>
  <modal :modal-id="modalId"
         modal-size="large"
         :title="selectedUrl ? $t('edit_shipment') : $t('add_shipment')"
         :preloader="preloader"
         @submit="submitData"
         @closeModal="closeModal">
    <template slot="body">
      <app-overlay-loader v-if="preloader"/>
      <form v-else class="mb-0"
            :class="{'loading-opacity': preloader}"
            ref="form"
            data-url="/shipments/save">

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label for="client_id">{{ $t('client') }} <sup class="text-size-20 top-1"></sup></label>
              <div class="d-flex align-items-center w-100">
                <app-input
                    id="client_id"
                    class="margin-right-8"
                    type="search-and-select"
                    :placeholder="$t('choose_a_client')"
                    :options="clientOptions"
                    v-model="formData.client_id"
                    :error-message="$errorMessage(errors, 'client_id')"
                />
                <button class="btn btn-primary ml-2" type="button" @click="openClientModal">
                  <app-icon name="plus" :size="16"/>
                </button>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="status_id">{{ $t('status') }} <sup class="text-size-20 top-1">*</sup></label>
              <app-input
                  id="status_id"
                  type="select"
                  v-model="formData.status_id"
                  :list="statues"
                  :required="true"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="port_type">{{ $t('port_type') }} <sup class="text-size-20 top-1">*</sup></label>
              <app-input
                  id="port_type"
                  type="select"
                  v-model="formData.port_type"
                  :list="portTypes"
                  :required="true"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="port">
                {{ $t('port') }} <sup class="text-size-20 top-1">*</sup>
              </label>
              <app-input
                  id="port"
                  type="text"
                  :placeholder="$t('enter_name')"
                  v-model="formData.port"
                  :error-message="$errorMessage(errors, 'port')"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="document_status_id">{{ $t('document_status') }} <sup class="text-size-20 top-1"></sup></label>
              <app-input
                  id="document_status_id"
                  type="select"
                  v-model="formData.document_status_id"
                  :list="documentStatus"
                  :required="true"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="document_status">
                {{ $t('document_status_if_other') }} <sup class="text-size-20 top-1"></sup>
              </label>
              <app-input
                  id="document_status"
                  type="text"
                  v-model="formData.document_status"
                  :error-message="$errorMessage(errors, 'document_status')"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="bill_of_loading">
                {{ $t('bill_of_loading') }} <sup class="text-size-20 top-1"></sup>
              </label>
              <app-input
                  id="bill_of_loading"
                  type="text"
                  v-model="formData.bill_of_loading"
                  :error-message="$errorMessage(errors, 'bill_of_loading')"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="declaration_number">
                {{ $t('declaration_number') }} <sup class="text-size-20 top-1"></sup>
              </label>
              <app-input
                  id="declaration_number"
                  type="text"
                  v-model="formData.declaration_number"
                  :error-message="$errorMessage(errors, 'declaration_number')"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="eta">
                {{ $t('eta') }}<sup class="text-size-20 top-1"></sup>
              </label>
              <app-input
                  id="eta"
                  type="date"
                  :placeholder="$t('enter_date')"
                  v-model="formData.eta"
                  :error-message="$errorMessage(errors, 'eta')"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="shipping_line">
                {{ $t('shipping_line') }}<sup class="text-size-20 top-1"></sup>
              </label>
              <app-input
                  id="shipping_line"
                  type="text"
                  v-model="formData.shipping_line"
                  :error-message="$errorMessage(errors, 'shipping_line')"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="consignee">
                {{ $t('consignee') }}<sup class="text-size-20 top-1"></sup>
              </label>
              <app-input
                  id="consignee"
                  type="text"
                  v-model="formData.consignee"
                  :error-message="$errorMessage(errors, 'consignee')"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="quantity">
                {{ $t('quantity') }}<sup class="text-size-20 top-1"></sup>
              </label>
              <app-input
                  id="quantity"
                  type="text"
                  v-model="formData.quantity"
                  :error-message="$errorMessage(errors, 'quantity')"
              />
            </div>
          </div>
          <div class="col-md-6">
            <label>&nbsp;</label>
            <app-input
                id="show_usd"
                type="single-checkbox"
                v-model="formData.require_permit"
                :list-value-field="$t('require_permit')"
            />
          </div>
          <div class="col-md-12 mb-3">
            <div class="form-group">
              <label for="description">{{ $t('goods_description') }}</label>
              <app-input
                  id="description"
                  type="textarea"
                  :text-area-rows="3"
                  :placeholder="$t('enter_note')"
                  v-model="formData.description"
              />
            </div>
          </div>
          <div class="col-md-12 mb-3">
            <div class="form-group">
              <label for="remarks">{{ $t('remarks') }}</label>
              <app-input
                  id="remarks"
                  type="textarea"
                  :text-area-rows="3"
                  :placeholder="$t('enter_note')"
                  v-model="formData.remarks"
              />
            </div>
          </div>

        </div>
      </form>
      <client-add-edit-modal
          v-if="isClientModalActive"
          :table-id="'client-table'"
          :client-number="clientNumber"
          @closeModal="closeClientModal"
      />
    </template>
  </modal>
</template>

<script>
import {SubmitFormMixins} from "../../../Mixins/billar/SubmitFormMixins";
import {axiosGet, urlGenerator} from "../../../Helpers/AxiosHelper";
import {formDataAssigner} from "../../../Helpers/Helpers";
import {formatDateForServer} from "../../../Helpers/DateTimeHelper";
import DateFunction from "../../../../core/helpers/date/DateFunction";


export default {
  name: "ShipmentCreateEditModal",
  mixins: [SubmitFormMixins],
  props: {
    statues: {},
    portTypes: {},
    documentStatus: {},
    clientNumber: '',
  },
  data() {
    return {
      formData: {
        eta: DateFunction.getDateFormat(new Date(), 'YYYY-MM-DD'),
      },
      isClientModalActive: false,
      expensePurposeList: [],
      files: [],
      maxFileWarning: false,
      modalId: 'shipment-create-edit-modal',
      clientOptions: {
        url: urlGenerator('client-users'),
        per_page: 10,
        loader: "app-pre-loader",
        modifire: (value) => ({id: value.id, value: value.full_name}),
      },
    }
  },
  methods: {
    submitData() {
      if (this.files.length > 1) {
        this.maxFileWarning = true;
        return;
      }
      this.maxFileWarning = false;
      this.formData.eta = formatDateForServer(this.formData?.eta);
      let formData = formDataAssigner(new FormData(), this.formData);
      if (this.selectedUrl) {
        this.submitFromFixin('post', 'shipments/update/' + this.formData.id, formData)
      } else {
        this.submitFromFixin('post', 'shipments/save', formData)
      }
    },
    afterSuccessFromGetEditData(response) {
      this.formData = response.data;
      this.preloader = false;
    },
    openClientModal() {
      this.isClientModalActive = true;
    },
    closeClientModal() {
      this.isClientModalActive = false;
    },
    setConsignee(input){
      this.formData.consignee = input;
    },
  },
  mounted() {
    let vueMethod = this;
    $(document).on('selectedItem', document, function(e, data){
      if(data.field === 'client_id'){
        vueMethod.setConsignee(data.data.value);
      }
    });
  },
}
</script>

<style scoped>

</style>
